import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import {
  COPY,
  EMPTY_SAFETY_FILTERS,
  PAGE_SIZE,
  SAFETY_GROUP_TRIP_PREVIEW_COPY,
  SAFETY_STATUS_OPTIONS,
  SAFETY_TARGET_OPTIONS,
  buildSafetyPageParams,
  canSubmitSafetyDecision,
  formatDateCell,
  formatGroupTripStatus,
  formatSafetyDecision,
  formatSafetyReason,
  formatSafetyStatus,
  formatSafetyTargetLabel,
  formatSafetyTechnicalId,
  formatSafetyVisibility,
  safetyTargetContext,
  formatEffectiveModerationState,
  effectiveModerationStateClassName,
  hasGroupTripSafetyPreview,
  isDestructiveSafetyDecision,
  isGroupTripSafetyTarget,
  isSafetyCaseOpen,
  resolveSafetyCaseId,
  safetyDecisionOptionsForTarget,
  safetyStatusClassName,
  safetyCaseId,
  shouldConfirmSafetyDecision,
} from './travelManageConstants';

const TABLE_COLUMN_COUNT = 7;

function SafetyTargetHierarchy({ report }) {
  const typeLabel = formatSafetyTargetLabel(report?.targetType);
  const context = safetyTargetContext(report);
  const technicalId = formatSafetyTechnicalId(report?.targetId);
  return (
    <div className="safety-target" data-testid="safety-target">
      <strong className="safety-target__type">{typeLabel}</strong>
      {context ? <span className="safety-target__context">{context}</span> : null}
      {technicalId ? (
        <small className="safety-target__id" data-testid="safety-target-id">
          技術 ID：{technicalId}
        </small>
      ) : null}
    </div>
  );
}

export default function SafetyReportsPage() {
  const [filters, setFilters] = useState(EMPTY_SAFETY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState(null);
  const [caseDraft, setCaseDraft] = useState(null);
  const [openingCase, setOpeningCase] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [confirmDecision, setConfirmDecision] = useState(false);

  async function load(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.travelSafetyReportPageQuery(
        buildSafetyPageParams(pageNo, nextFilters, PAGE_SIZE),
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    load(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_SAFETY_FILTERS };
    setFilters(reset);
    setError('');
    setMessage('');
    load(1, reset);
  }

  function closeModal() {
    setSelected(null);
    setCaseDraft(null);
    setConfirmDecision(false);
    setOpeningCase(false);
    setDeciding(false);
  }

  async function openReport(reportId) {
    setError('');
    setMessage('');
    setConfirmDecision(false);
    try {
      const data = await adminApi.travelSafetyReportDetail(reportId);
      setSelected(data);
      setCaseDraft(null);
    } catch (err) {
      setError(err.message || '載入詳情失敗');
    }
  }

  async function openCase() {
    if (!selected || openingCase) return;
    setError('');
    setOpeningCase(true);
    try {
      const data = await adminApi.travelSafetyOpenCase({
        targetType: selected.targetType,
        targetId: selected.targetId,
      });
      const resolvedCaseId = resolveSafetyCaseId(data);
      if (!resolvedCaseId) {
        throw new Error('開案成功但未取得案件 ID，請重新整理後再試');
      }
      const status = data.status || 'OPEN';
      setCaseDraft({
        ...data,
        caseId: resolvedCaseId,
        id: resolvedCaseId,
        status,
        decision: isGroupTripSafetyTarget(selected.targetType)
          ? 'NO_ACTION'
          : (data.decision || 'NO_ACTION'),
        reason: data.reason || selected.reason || '',
        expectedRowVersion: data.rowVersion ?? data.row_version ?? 1,
      });
      setMessage(status === 'RESOLVED'
        ? '此檢舉對象已有裁決案件，無法重複提交'
        : '已開啟審核案件');
    } catch (err) {
      setError(err.message || '開案失敗');
    } finally {
      setOpeningCase(false);
    }
  }

  function requestSubmitDecision() {
    if (!canSubmitSafetyDecision({ deciding, caseDraft })) {
      return;
    }
    if (shouldConfirmSafetyDecision(caseDraft.decision)) {
      setConfirmDecision(true);
      return;
    }
    submitDecision();
  }

  async function submitDecision() {
    if (!canSubmitSafetyDecision({ deciding, caseDraft })) {
      return;
    }
    setDeciding(true);
    setError('');
    try {
      await adminApi.travelSafetyDecideCase(safetyCaseId(caseDraft), {
        decision: caseDraft.decision,
        reason: caseDraft.reason,
        expectedRowVersion: caseDraft.expectedRowVersion
          ?? caseDraft.row_version
          ?? caseDraft.rowVersion
          ?? 1,
      });
      setMessage('裁決已提交');
      setConfirmDecision(false);
      closeModal();
      load(page, filters);
    } catch (err) {
      setError(err.message || '裁決失敗');
    } finally {
      setDeciding(false);
    }
  }

  const submitDisabled = !canSubmitSafetyDecision({ deciding, caseDraft });
  const decisionIsDestructive = isDestructiveSafetyDecision(caseDraft?.decision);
  const decisionOptions = safetyDecisionOptionsForTarget(selected?.targetType);
  const showGroupTripPreview = selected && isGroupTripSafetyTarget(selected.targetType);
  const groupTripPreviewReady = hasGroupTripSafetyPreview(selected);

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>安全審核</h2>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="sfStatus">{COPY.status}</label>
          <select
            id="sfStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            {SAFETY_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="sfTargetType">檢舉對象類型</label>
          <select
            id="sfTargetType"
            value={filters.targetType}
            onChange={(e) => updateFilter('targetType', e.target.value)}
          >
            {SAFETY_TARGET_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="sfTargetId">檢舉對象 ID</label>
          <input
            id="sfTargetId"
            value={filters.targetId}
            onChange={(e) => updateFilter('targetId', e.target.value)}
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">{COPY.search}</button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            {COPY.reset}
          </button>
        </div>
      </form>

      {loading ? <p>載入中…</p> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}
      {message ? <div className="alert alert-success">{message}</div> : null}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>檢舉 ID</th>
              <th>檢舉人</th>
              <th>檢舉對象</th>
              <th>原因</th>
              <th>{COPY.status}</th>
              <th>{COPY.createdAt}</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMN_COUNT} className="empty-cell">
                  {COPY.emptyList}
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.reportId}>
                  <td>{row.reportId}</td>
                  <td>{row.reporterMemberId}</td>
                  <td><SafetyTargetHierarchy report={row} /></td>
                  <td>{formatSafetyReason(row.reason)}</td>
                  <td className={safetyStatusClassName(row.status)}>
                    {formatSafetyStatus(row.status)}
                  </td>
                  <td>{formatDateCell(row.createTime)}</td>
                  <td>
                    <button type="button" className="btn btn-secondary" onClick={() => openReport(row.reportId)}>
                      處理
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => load(p, filters)} />

      {selected ? (
        <div className="confirm-dialog-overlay" role="presentation" onClick={closeModal}>
          <div
            className="modal-panel"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-panel__header">
              <h3>處理檢舉 #{selected.reportId}</h3>
            </div>
            <p>檢舉對象：</p>
            <SafetyTargetHierarchy report={selected} />
            <p>原因：{formatSafetyReason(selected.reason)}</p>
            <p>
              {COPY.status}：
              <span className={safetyStatusClassName(selected.status)}>
                {formatSafetyStatus(selected.status)}
              </span>
            </p>

            {showGroupTripPreview ? (
              <section
                className="safety-group-trip-preview"
                data-testid="safety-group-trip-preview"
                aria-label={SAFETY_GROUP_TRIP_PREVIEW_COPY.section}
              >
                <h4>{SAFETY_GROUP_TRIP_PREVIEW_COPY.section}</h4>
                {groupTripPreviewReady ? (
                  <dl className="safety-group-trip-preview__fields">
                    <div>
                      <dt>{SAFETY_GROUP_TRIP_PREVIEW_COPY.title}</dt>
                      <dd data-testid="gt-preview-title">{selected.recruitmentTitle || '—'}</dd>
                    </div>
                    <div>
                      <dt>{SAFETY_GROUP_TRIP_PREVIEW_COPY.summary}</dt>
                      <dd data-testid="gt-preview-summary" className="safety-group-trip-preview__summary">
                        {selected.summaryPreview || '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>{SAFETY_GROUP_TRIP_PREVIEW_COPY.host}</dt>
                      <dd data-testid="gt-preview-host">{selected.hostMemberId || '—'}</dd>
                    </div>
                    <div>
                      <dt>{SAFETY_GROUP_TRIP_PREVIEW_COPY.status}</dt>
                      <dd data-testid="gt-preview-status">
                        {formatGroupTripStatus(selected.recruitmentStatus)}
                      </dd>
                    </div>
                    <div>
                      <dt>{SAFETY_GROUP_TRIP_PREVIEW_COPY.visibility}</dt>
                      <dd data-testid="gt-preview-visibility">
                        {formatSafetyVisibility(selected.visibility)}
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <p className="safety-group-trip-preview__missing" role="status">
                    {SAFETY_GROUP_TRIP_PREVIEW_COPY.missing}
                  </p>
                )}
                <p className="safety-group-trip-preview__hint" data-testid="gt-no-action-hint">
                  {SAFETY_GROUP_TRIP_PREVIEW_COPY.noActionHint}
                </p>
              </section>
            ) : null}

            {!caseDraft ? (
              <div className="modal-panel__footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  取消
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={openingCase}
                  onClick={openCase}
                >
                  {openingCase ? '開案中…' : '開案'}
                </button>
              </div>
            ) : (
              <div className="modal-panel__body">
                <p>案件 #{safetyCaseId(caseDraft)}</p>
                <p>
                  案件狀態：
                  <span className={safetyStatusClassName(caseDraft.status)}>
                    {formatSafetyStatus(caseDraft.status || 'OPEN')}
                  </span>
                </p>
                <p data-testid="safety-effective-state">
                  生效審核狀態：
                  <span className={effectiveModerationStateClassName(caseDraft.effectiveModerationState)}>
                    {formatEffectiveModerationState(caseDraft.effectiveModerationState)}
                  </span>
                </p>
                {!isSafetyCaseOpen(caseDraft) ? (
                  <div className="alert alert-error">
                    此案件已結案
                    {caseDraft.decision ? `（${formatSafetyDecision(caseDraft.decision)}）` : ''}
                    ，無法再次裁決。同一檢舉對象只會共用一筆 moderation case。
                  </div>
                ) : null}
                <fieldset className="safety-decision-options" disabled={!isSafetyCaseOpen(caseDraft)}>
                  <legend>裁決</legend>
                  {decisionOptions.map((opt) => (
                    <label key={opt.value} className="safety-decision-options__item">
                      <input
                        type="radio"
                        name="safety-decision"
                        value={opt.value}
                        checked={(caseDraft.decision || 'NO_ACTION') === opt.value}
                        disabled={!isSafetyCaseOpen(caseDraft)}
                        onChange={() => {
                          setConfirmDecision(false);
                          setCaseDraft({ ...caseDraft, decision: opt.value });
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </fieldset>
                <label className="form-field">
                  裁決原因
                  <input
                    value={caseDraft.reason || ''}
                    disabled={!isSafetyCaseOpen(caseDraft)}
                    onChange={(e) => setCaseDraft({ ...caseDraft, reason: e.target.value })}
                    placeholder="可填寫 SPAM 等既有 reason code"
                  />
                </label>

                {confirmDecision ? (
                  <div className="alert alert-error">
                    確認提交裁決「{formatSafetyDecision(caseDraft.decision)}」？此操作可能限制或移除內容曝光。
                  </div>
                ) : null}

                <div className="modal-panel__footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={deciding}>
                    取消
                  </button>
                  {isSafetyCaseOpen(caseDraft) && confirmDecision ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={deciding}
                        onClick={() => setConfirmDecision(false)}
                      >
                        返回
                      </button>
                      <button
                        type="button"
                        className={`btn ${decisionIsDestructive ? 'btn-danger' : 'btn-primary'}`}
                        disabled={deciding}
                        onClick={submitDecision}
                      >
                        {deciding ? '提交中…' : '確認提交'}
                      </button>
                    </>
                  ) : null}
                  {isSafetyCaseOpen(caseDraft) && !confirmDecision ? (
                    <button
                      type="button"
                      className={`btn ${decisionIsDestructive ? 'btn-warning' : 'btn-primary'}`}
                      disabled={submitDisabled}
                      onClick={requestSubmitDecision}
                    >
                      提交裁決
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
